import React, { useState } from "react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from "~/components/shadcn/Command";
import { Badge } from "~/components/shadcn/Badge";
import { RiUserAddLine } from "react-icons/ri";
import { RxCheck } from "react-icons/rx";
import { Button } from "~/components/v3/themed/Button";
import { cn } from "~/utils/fontsConfig";
import { If } from "~/components/v3/Conditional";

type OptionType = { value: string; count: number } | string;

interface FacetedFilterSelectorProps {
    placeholder: string;
    onFilterChange: (value: string) => void;
    onSelectonChange: (tags: string[]) => void;
    options: OptionType[];
    ref?: React.RefObject<{ clearSelections: () => void }>;
    className?: string;
}

const FacetedFilterSelector = React.forwardRef<
    { clearSelections: () => void },
    FacetedFilterSelectorProps
>(
    (
        { placeholder, onFilterChange, onSelectonChange, options, className },
        ref,
    ) => {
        const [open, setOpen] = useState(false);
        const [selectedValues, setSelectedValues] = useState<OptionType[]>([]);

        React.useImperativeHandle(ref, () => ({
            clearSelections: () => {
                setSelectedValues([]);
                onSelectonChange([]);
            },
        }));

        const handleSelection = (option: OptionType) => {
            const newSelectedValues = selectedValues.includes(option)
                ? selectedValues.filter((e) => e !== option)
                : [...selectedValues, option];

            setSelectedValues(newSelectedValues);
            onSelectonChange(
                newSelectedValues.map((item) =>
                    typeof item === "string" ? item : item.value,
                ),
            );
        };

        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        role="combobox"
                        aria-expanded={open}
                        variant="ghost"
                        className={cn(
                            "flex h-auto min-h-[2.5rem]",
                            "flex-col justify-center rounded-md",
                            "border border-v3-N-500 p-2 text-xs hover:bg-v3-N-500",
                            className,
                        )}
                    >
                        <div className="flex grow-0 items-center gap-2">
                            <RiUserAddLine className="h-4 w-4 flex-shrink-0" />
                            <span>{placeholder}</span>
                        </div>
                        {selectedValues.length > 0 && (
                            <div className="mt-1 flex grow flex-wrap gap-1">
                                {selectedValues.map((value, i) => {
                                    const val =
                                        typeof value === "string"
                                            ? value
                                            : value.value;
                                    return (
                                        <Badge
                                            key={val}
                                            variant="secondary"
                                            className="truncate rounded-md px-2 font-normal"
                                        >
                                            {val}
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[330px] bg-primary p-0 md:w-[400px]"
                    align="start"
                >
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={placeholder}
                            onValueChange={onFilterChange}
                        />
                        <CommandEmpty>Nothing found.</CommandEmpty>
                        <CommandGroup className="max-h-[300px] overflow-y-auto">
                            {options.map((option, index) => {
                                const isString = typeof option === "string";
                                const value = isString ? option : option.value;
                                const isSelected =
                                    selectedValues.includes(option);
                                return (
                                    <CommandItem
                                        key={index}
                                        onSelect={() => handleSelection(option)}
                                    >
                                        <div
                                            className={cn(
                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                isSelected
                                                    ? "bg-primary text-v3-N-300"
                                                    : "opacity-50 [&_svg]:invisible",
                                            )}
                                        >
                                            <RxCheck
                                                className={cn("h-4 w-4")}
                                            />
                                        </div>
                                        <span>{value}</span>
                                        <If condition={!isString}>
                                            <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                                                {!isString && option.count}
                                            </span>
                                        </If>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </Command>
                </PopoverContent>
            </Popover>
        );
    },
);

FacetedFilterSelector.displayName = "FacetedFilterSelector";

export default FacetedFilterSelector;
